import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
  NormalizedCacheObject,
} from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import { getFirebaseUser } from 'src/firebase'
import fragmentTypes from 'src/fragmentTypes.json'
import { store } from '../redux/store'
import { consoleSessionEnd, isConsoleSessionRefusal } from './consoleSessionEnd'

const { possibleTypes } = fragmentTypes

/**
 * The normalisation rules the console's cache runs on.
 *
 * Exported because they are what decides whether a `cache-and-network` screen
 * actually draws its previous rows: a type with no key here is stored under
 * whoever fetched it and shared with nobody. A spec that builds its own
 * `InMemoryCache` would be measuring a cache the browser never uses.
 */
export const createCache = () =>
  new InMemoryCache({
    possibleTypes,
    typePolicies: {
      VehiclePicture: { keyFields: ['url2x'] },
      VehicleStatus: { keyFields: ['id'] },
      KycVerification: { keyFields: ['id'] },
      UserBalance: { keyFields: ['userId'] },
      Location: { keyFields: ['latitude', 'longitude'] },
      Address: { keyFields: ['displayString'] },
      BookingLocation: { keyFields: ['id'] },
      VehicleBookingLocation: { keyFields: ['id'] },
      // Always the signed-in person's own, and without an id: Settings asks
      // for it in two queries, and each answer is merged into the other
      // rather than replacing it, which sent the two into asking again in
      // turn.
      ConsoleAccount: { merge: true },
    },
  })

const cache = createCache()

const httpLink = new HttpLink({
  fetchOptions: {
    mode: 'cors',
  },
})

const withToken = setContext(async (_operation, context) => {
  if (context.anonymous) {
    return context
  }

  const user = getFirebaseUser()

  if (!user) {
    throw new Error('Please login')
  }

  const token = await user.getIdToken()
  const providerId = store.getState().providers?.providerId

  return { ...context, token, providerId }
})

const endpointMiddleware = new ApolloLink((operation, forward) => {
  const endpoint = import.meta.env.VITE_BACKOFFICE_URL

  operation.setContext(() => ({
    uri: `${endpoint}/graphql`,
  }))

  return forward(operation)
})

const authMiddleware = new ApolloLink((operation, forward) => {
  const { token, providerId } = operation.getContext()

  if (token) {
    operation.setContext(({ headers = {} }) => ({
      headers: {
        ...headers,
        authorization: `Bearer ${token}`,
      },
    }))
  }

  if (providerId) {
    operation.setContext(({ headers = {} }) => ({
      headers: {
        ...headers,
        'x-provider-id': providerId,
      },
    }))
  }

  return forward(operation)
})

/**
 * Signs out when the backend refuses the session itself. A 401 with
 * `CONSOLE_SESSION_REQUIRED` is not a failed request to retry; see
 * `consoleSessionEnd`.
 */
export const sessionRefusedLink = onError(({ networkError }) => {
  if (
    networkError &&
    'statusCode' in networkError &&
    networkError.statusCode === 401 &&
    'result' in networkError &&
    isConsoleSessionRefusal(networkError.result)
  ) {
    void consoleSessionEnd()
  }
})

const link = ApolloLink.from([
  sessionRefusedLink,
  withToken,
  authMiddleware,
  endpointMiddleware,
  httpLink,
])

const getClient = (): ApolloClient<NormalizedCacheObject> => {
  return new ApolloClient({
    cache,
    link,
  })
}

export default getClient
